import type { FC, ReactElement } from "react";
import { useCallback } from "react";
import type { IDropdownChild } from "@similarweb/ui-components";
import { Dropdown, DropdownButton, SimpleDropdownItem } from "@similarweb/ui-components";
import styled, { createGlobalStyle } from "styled-components";
import { setFont } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";

interface DashboardSearchItem {
    id: string;
    title: string;
}

interface DashboardSearchHeaderProps {
    dashboardList: Array<DashboardSearchItem>;
    clickHandler: (id: string) => void;
    currentDashboardId: string;
}

const createDropdownItems = (items: Array<DashboardSearchItem>): Array<IDropdownChild> => {
    return items.map((item) => (
        <SimpleDropdownItem
            key={item.id}
            id={item.id}
        >
            {item.title}
        </SimpleDropdownItem>
    ));
};

const getSelectedItemText = (selectedItemId: string, itemList: Array<DashboardSearchItem>): string => {
    const matchingItems = itemList.filter((item) => item.id === selectedItemId);
    if (matchingItems.length === 1) {
        return matchingItems[0].title;
    }

    return "";
};

const DashboardSearchDropdownWrapper = styled.div`
    width: 45%;
    justify-content: flex-end;
    @media (max-width: 1200px) {
        width: 35%;
        margin-right: 30px;
    }
`;

const DashboardTitle = styled.h4`
    ${setFont({
        $color: colorsPalettes.carbon[500],
        $size: 24,
        $weight: 500,
        $family: "DM Sans",
    })}
    width: 95%;
    padding-left: 15px;
    padding-top: 8px;
    padding-bottom: 8px;
`;

const ButtonStyle = createGlobalStyle`
    .dashboard-title-button.DropdownButton {
        transition: none;
        &:active {
            background-color: ${colorsPalettes.carbon[50]};
        }

        &:hover {
            border: none;
            box-shadow: none;
            background-color: ${colorsPalettes.carbon[50]};
        }
    }

    .dashboard-title-button > .DropdownButton-text {
        text-indent: 15px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        overflow-wrap: break-word;
    }

    .dashboard-title-button.DropdownButton--opened {
        background-color: ${colorsPalettes.carbon[25]};
    }

    .dashboard-title-button.DropdownButton--focus {
        background-color: ${colorsPalettes.carbon[25]};
    }
`;

const createDropdownButton = (selectedItemId: string, itemList: Array<DashboardSearchItem>): ReactElement => {
    const buttonText = getSelectedItemText(selectedItemId, itemList);
    return (
        <DropdownButton
            key="item0"
            height={"auto"}
            cssClass={"dashboard-title-button"}
        >
            <ButtonStyle />
            <DashboardTitle>{buttonText}</DashboardTitle>
        </DropdownButton>
    );
};

export const DashboardSearchHeader: FC<DashboardSearchHeaderProps> = ({
    dashboardList,
    clickHandler,
    currentDashboardId,
}) => {
    const dropdownItems = [
        createDropdownButton(currentDashboardId, dashboardList),
        ...createDropdownItems(dashboardList),
    ];

    const searchItemClick = useCallback(
        (item) => {
            clickHandler(item);
        },
        [clickHandler],
    );

    return (
        <DashboardSearchDropdownWrapper>
            <Dropdown
                selectedIds={{ [currentDashboardId]: true }}
                onClick={searchItemClick}
                appendTo=".dashboard-header"
                hasSearch={true}
                virtualize={true}
                searchPlaceHolder={"Search dashboards"}
            >
                {dropdownItems}
            </Dropdown>
        </DashboardSearchDropdownWrapper>
    );
};
